/**
 * Calendar days, kept as calendar days.
 *
 * WHY `toISOString().slice(0, 10)` IS NOT USED ANYWHERE HERE. It converts to UTC first.
 * For a merchant in Asia/Kolkata (UTC+5:30) any moment before 05:30 local is still
 * YESTERDAY in UTC, so the "last 7 days" a merchant opened at 9am would silently ask for
 * a window ending the previous day — a whole day of orders missing from a report that
 * looked entirely normal. Every function below reads the LOCAL date parts instead.
 *
 * These are days in the BROWSER's zone, which is only a starting guess at the store's.
 * The API answers in the store's zone and echoes it back, and the screen shows which zone
 * the numbers came from rather than pretending the two always agree.
 */

/** `YYYY-MM-DD` from the local calendar, never via UTC. */
export function toLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** `days` before `date`, as a calendar day. Handles month and year edges via Date itself. */
export function daysBefore(date: Date, days: number): Date {
  const shifted = new Date(date.getTime());
  shifted.setDate(shifted.getDate() - days);
  return shifted;
}

export interface DateRange {
  readonly from: string;
  readonly to: string;
}

/** The window a report opens on: the last 7 days, today included. */
export function defaultRange(today: Date = new Date()): DateRange {
  return { from: toLocalDate(daysBefore(today, 6)), to: toLocalDate(today) };
}

/**
 * Checked HERE, before the request, so a reversed range is a sentence the merchant can
 * act on instead of a 400 rendered as "Request failed with status code 400".
 *
 * String comparison is correct and deliberate for `YYYY-MM-DD`: zero-padded fixed-width
 * fields sort identically as text and as dates, with no Date parsing to get wrong.
 */
export function rangeProblem(range: DateRange): string | null {
  const shape = /^\d{4}-\d{2}-\d{2}$/;
  if (!shape.test(range.from) || !shape.test(range.to)) return "Pick both a start and an end date.";
  if (range.from > range.to) return "The start date is after the end date.";
  return null;
}

/**
 * The quick ranges, taken from legacy's `CustomDateRangePicker.jsx`.
 *
 * ITS DEFINITIONS, NOT SIMILAR ONES: legacy's "Last 7 days" is `today - 6 … today`,
 * INCLUSIVE of today — seven days, not eight and not "the seven days before today". A
 * merchant comparing the two dashboards on the same afternoon must get the same window,
 * or every number differs and neither app looks trustworthy.
 */
export type PresetKey = "today" | "yesterday" | "last7" | "last14" | "last28" | "thisMonth" | "lastMonth";

export const PRESETS: readonly { readonly key: PresetKey; readonly label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 days" },
  { key: "last14", label: "Last 14 days" },
  { key: "last28", label: "Last 28 days" },
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
];

export function presetRange(key: PresetKey, today: Date = new Date()): DateRange {
  switch (key) {
    case "today":
      return { from: toLocalDate(today), to: toLocalDate(today) };
    case "yesterday": {
      const day = toLocalDate(daysBefore(today, 1));
      return { from: day, to: day };
    }
    case "last7":
      return { from: toLocalDate(daysBefore(today, 6)), to: toLocalDate(today) };
    case "last14":
      return { from: toLocalDate(daysBefore(today, 13)), to: toLocalDate(today) };
    case "last28":
      return { from: toLocalDate(daysBefore(today, 27)), to: toLocalDate(today) };
    case "thisMonth":
      // Ends TODAY, not at the end of the month: a range running into the future would
      // ask the API for days that have not happened.
      return { from: toLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: toLocalDate(today) };
    case "lastMonth": {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      // Day 0 of the following month IS the last day of this one — no month-length table
      // and no leap-year special case.
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: toLocalDate(first), to: toLocalDate(last) };
    }
  }
}

/** Which preset a range corresponds to, or null when it is a custom window. */
export function matchPreset(range: DateRange, today: Date = new Date()): PresetKey | null {
  for (const { key } of PRESETS) {
    const candidate = presetRange(key, today);
    if (candidate.from === range.from && candidate.to === range.to) return key;
  }
  return null;
}

/**
 * "Aug 29 – Sep 4", the way legacy labels its picker.
 *
 * THE YEAR IS SHOWN ONLY WHEN IT MATTERS — when either end falls outside the current
 * year. Legacy always omits it, which reads fine in September and is ambiguous in January
 * when someone is looking at last December.
 */
export function formatRange(range: DateRange, today: Date = new Date()): string {
  const parts = (iso: string): { date: Date; year: number } | null => {
    const [year, month, day] = iso.split("-").map(Number);
    if (year === undefined || month === undefined || day === undefined) return null;
    // Constructed from PARTS, never `new Date(iso)` — that parses a bare date as UTC and
    // can render the previous day for anyone west of Greenwich.
    return { date: new Date(year, month - 1, day), year };
  };

  const from = parts(range.from);
  const to = parts(range.to);
  if (from === null || to === null) return `${range.from} – ${range.to}`;

  const showYear = from.year !== today.getFullYear() || to.year !== today.getFullYear();
  const label = (date: Date): string =>
    date.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(showYear ? { year: "numeric" } : {}) });

  return range.from === range.to ? label(from.date) : `${label(from.date)} – ${label(to.date)}`;
}

const RANGE_KEY = "ecomally.dateRange";

/**
 * The date range a merchant chose, remembered ACROSS REPORTS.
 *
 * Each report screen remounts when you switch reports — deliberately, so one report's
 * rows can never appear under another's headers — which meant the window reset to the
 * default every time. Someone comparing last month across three reports had to re-pick
 * the dates on each one, and the third page silently answered a different question from
 * the first.
 *
 * SESSION storage, not local. An absolute range is only meaningful for as long as you are
 * working with it: reopening the dashboard next week to find it still pinned to
 * "1–7 September" is worse than starting from the default, because the stale dates look
 * like today's. The selected STORE is remembered in localStorage — that choice does not go
 * stale the same way.
 */
export function rememberRange(range: DateRange): void {
  try {
    window.sessionStorage.setItem(RANGE_KEY, JSON.stringify(range));
  } catch (error) {
    // Safari in private mode throws on setItem. Losing a remembered preference must
    // never break the page that was about to render — same handling as the store picker.
    void error;
  }
}

/** The remembered range, or the default when there is none or it is unusable. */
export function rememberedRange(fallback: DateRange = defaultRange()): DateRange {
  try {
    const raw = window.sessionStorage.getItem(RANGE_KEY);
    if (raw === null) return fallback;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return fallback;

    const { from, to } = parsed as { from?: unknown; to?: unknown };
    if (typeof from !== "string" || typeof to !== "string") return fallback;

    // VALIDATED, not trusted. Storage is editable and survives deploys, so a range saved
    // by an older build — or by hand — must not become a 400 on every report.
    const candidate = { from, to };
    return rangeProblem(candidate) === null ? candidate : fallback;
  } catch (error) {
    void error;
    return fallback;
  }
}
